<template>
  <div class="menu-wrapper">
    <!-- Logo区域 -->
    <div class="logoContent">
      <div class="logo-container">
        <img
          class="logo"
          src="../../../../../static/images/indexLogo.png"
          alt="京澜"
        />
        <span class="logo-text">京澜管理后台</span>
      </div>
    </div>

    <!-- 菜单区域 - 恢复水平排列 -->
    <div class="menuContent">
      <div
        v-for="item in visibleRoutes"
        :key="`l1_${item.name}_${item.path}`"
        class="menu-item"
        @mouseover="showSubmenu(item.name); handleMouseEnter(item)"
        @mouseleave="hideSubmenu(item.name); handleMouseLeave()"
      >
        <button
          type="button"
          class="menu-item-link"
          aria-haspopup="true"
          :aria-expanded="isSubmenuVisible(item.name) || (mobileMenuOpen && mobileExpandedMenu === item.name)"
          @click.stop.prevent="showSubmenu(item.name)"
        >
          <div class="menu-item-content" :class="{ 'is-active': isActive(item) }">
            <span class="menu-text">{{ item.meta && item.meta.title }}</span>
            <i class="icon-arrow"></i>
          </div>
        </button>
        
        <!-- 下拉菜单 - 恢复多列布局 -->
        <div
          class="submenu-children"
          :class="{ 'is-direct-page-menu': isDirectPageMenu(item), 'is-wide-menu': isWideSubmenu(item) }"
          v-if="isSubmenuVisible(item.name)"
          :ref="`submenu_${item.name}`"
        >
          <div v-if="isDirectPageMenu(item)" class="direct-page-list">
            <router-link
              v-for="child in visibleChildren(item.children)"
              :key="`direct_l2_${child.name}_${child.path}`"
              :to="getSecondLevelTargetPath(item, child)"
              class="direct-page-link"
              :class="{ 'is-active': isActiveDirectPage(item, child) }"
            >
              <i :class="['direct-page-icon', getDirectPageIcon(child)]"></i>
              <span>{{ child.meta && child.meta.title }}</span>
              <i class="el-icon-arrow-right"></i>
            </router-link>
          </div>

          <div v-else class="submenu-grid">
            <div
              v-for="child in visibleChildren(item.children)"
              :key="`l2_${child.name}_${child.path}`"
              class="submenu-column"
            >
              <div class="submenu-column-header">
                <router-link 
                  :to="getSecondLevelTargetPath(item, child)" 
                  class="column-title-link"
                  :class="{ 'is-active': isActiveChild(child) }"
                >
                  <i class="column-icon el-icon-folder-opened"></i>
                  <span class="column-title">{{ child.meta && child.meta.title }}</span>
                </router-link>
              </div>
              
              <!-- 三级菜单 - 恢复垂直排列 -->
              <div v-if="child.children && child.children.length > 0" class="third-level-list">
                <router-link
                  v-for="subChild in visibleThirdChildren(child)"
                  :key="`l3_${subChild.name}_${subChild.path}`"
                  :to="generatePath(child, subChild)"
                  class="third-level-item"
                  :class="{ 'is-active': $route.path === generatePath(child, subChild) }"
                >
                  <i class="icon-dot"></i>
                  <span>{{ subChild.meta && subChild.meta.title }}</span>
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 用户操作区域 -->
    <div class="userContent">
      <!-- 通知 -->
      <el-dropdown
        trigger="click"
        class="dropdown-notice"
        @visible-change="handleNoticeVisibleChange"
      >
        <button type="button" class="notice-wrapper" title="通知消息" aria-label="通知消息">
          <el-badge 
            :value="noticeCount" 
            :hidden="noticeCount <= 0"
            :max="99"
          >
            <i class="icon-notice el-icon-bell"></i>
          </el-badge>
        </button>
        <el-dropdown-menu slot="dropdown" class="notice-dropdown-menu">
          <el-dropdown-item class="notice-dropdown-item">
            <Message ref="noticeMessage" @notice-change="getNoticeCount" />
          </el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>

      <!-- 用户信息 -->
      <el-dropdown trigger="click" class="dropdown-user">
        <button type="button" class="user-wrapper" aria-label="账号菜单">
          <el-avatar :size="32" :src="avatar" class="user-avatar">
            {{ nickName && nickName.charAt(0) }}
          </el-avatar>
          <div class="user-info">
            <span class="user-name">{{ nickName }}</span>
            <span class="user-role">管理员</span>
          </div>
          <i class="icon-arrow-down"></i>
        </button>
        <el-dropdown-menu slot="dropdown" class="user-dropdown-menu">
          <el-dropdown-item class="dropdown-header">
            <div class="user-profile">
              <el-avatar :size="40" :src="icon" class="profile-avatar">
                {{ nickName && nickName.charAt(0) }}
              </el-avatar>
              <div class="profile-info">
                <span class="profile-name">{{ nickName }}</span>
                <span class="profile-email">{{ emailName }}</span>
              </div>
            </div>
          </el-dropdown-item>
          
          <el-dropdown-item divided>
            <div class="dropdown-item" @click="handleModifyPassword">
              <i class="icon-setting el-icon-setting"></i>
              <span>修改密码</span>
            </div>
          </el-dropdown-item>
          
          <el-dropdown-item>
            <div class="dropdown-item" @click="logout">
              <i class="icon-logout el-icon-back"></i>
              <span>退出登录</span>
            </div>
          </el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </div>

    <!-- 手机端菜单入口：使用整屏纵向导航，避免多级菜单被横向容器裁切 -->
    <button
      type="button"
      class="mobile-menu-toggle"
      :class="{ 'is-open': mobileMenuOpen }"
      :aria-expanded="String(mobileMenuOpen)"
      aria-controls="mobile-navigation-panel"
      :aria-label="mobileMenuOpen ? '关闭导航菜单' : '打开导航菜单'"
      @click="toggleMobileMenu"
    >
      <span></span>
      <span></span>
      <span></span>
    </button>

    <transition name="mobile-menu-fade">
      <div v-if="mobileMenuOpen" class="mobile-menu-backdrop" @click.self="closeMobileMenu">
        <nav id="mobile-navigation-panel" class="mobile-menu-panel" aria-label="手机端主导航">
          <div class="mobile-menu-heading">
            <div>
              <strong>功能导航</strong>
              <span>选择模块后进入对应业务页面</span>
            </div>
            <button type="button" aria-label="关闭导航菜单" @click="closeMobileMenu">
              <i class="el-icon-close"></i>
            </button>
          </div>

          <div class="mobile-nav-list">
            <section
              v-for="item in visibleRoutes"
              :key="`mobile_l1_${item.name}_${item.path}`"
              class="mobile-nav-section"
              :class="{ 'is-active': isActive(item), 'is-expanded': mobileExpandedMenu === item.name }"
            >
              <button
                type="button"
                class="mobile-nav-primary"
                :aria-expanded="String(mobileExpandedMenu === item.name)"
                @click="toggleMobileSection(item.name)"
              >
                <span class="mobile-nav-primary-icon">{{ getMenuInitial(item) }}</span>
                <span class="mobile-nav-primary-text">{{ item.meta && item.meta.title }}</span>
                <i class="el-icon-arrow-down"></i>
              </button>

              <div v-show="mobileExpandedMenu === item.name" class="mobile-nav-children">
                <template v-if="isDirectPageMenu(item)">
                  <router-link
                    v-for="child in visibleChildren(item.children)"
                    :key="`mobile_direct_l2_${child.name}_${child.path}`"
                    :to="getSecondLevelTargetPath(item, child)"
                    class="mobile-nav-page"
                    :class="{ 'is-active': isActiveDirectPage(item, child) }"
                    @click.native="closeMobileMenu"
                  >
                    <i :class="getDirectPageIcon(child)"></i>
                    <span>{{ child.meta && child.meta.title }}</span>
                    <i class="el-icon-arrow-right"></i>
                  </router-link>
                </template>

                <template v-else>
                  <div
                    v-for="child in visibleChildren(item.children)"
                    :key="`mobile_l2_${child.name}_${child.path}`"
                    class="mobile-nav-group"
                  >
                    <router-link
                      :to="getSecondLevelTargetPath(item, child)"
                      class="mobile-nav-secondary"
                      :class="{ 'is-active': isActiveChild(child) }"
                      @click.native="closeMobileMenu"
                    >
                      <i class="el-icon-folder-opened"></i>
                      <span>{{ child.meta && child.meta.title }}</span>
                      <i class="el-icon-arrow-right"></i>
                    </router-link>

                    <div v-if="child.children && child.children.length" class="mobile-nav-third-list">
                      <router-link
                        v-for="subChild in visibleThirdChildren(child)"
                        :key="`mobile_l3_${subChild.name}_${subChild.path}`"
                        :to="generatePath(child, subChild)"
                        class="mobile-nav-third"
                        :class="{ 'is-active': $route.path === generatePath(child, subChild) }"
                        @click.native="closeMobileMenu"
                      >
                        <i class="icon-dot"></i>
                        <span>{{ subChild.meta && subChild.meta.title }}</span>
                      </router-link>
                    </div>
                  </div>
                </template>
              </div>
            </section>
          </div>
        </nav>
      </div>
    </transition>

    <!-- 修改密码弹窗 -->
    <el-dialog custom-class="jl-mobile-compact-dialog"
      class="password-dialog"
      title="修改密码"
      :visible.sync="dialogVisible"
      width="400px"
      :append-to-body="true"
    >
      <el-form 
        label-position="top" 
        :model="editFormData" 
        label-width="120px"
        :rules="passwordRules"
        ref="passwordForm"
      >
        <el-form-item label="旧密码" prop="oldPassword">
          <el-input 
            placeholder="请输入旧密码" 
            v-model="editFormData.oldPassword"
            type="password"
            show-password
          ></el-input>
        </el-form-item>
        <el-form-item label="新密码" prop="newPassword">
          <el-input 
            placeholder="请输入新密码" 
            v-model="editFormData.newPassword"
            type="password"
            show-password
          ></el-input>
        </el-form-item>
        <el-form-item label="确认密码" prop="confirmPassword">
          <el-input 
            placeholder="请确认新密码" 
            v-model="editFormData.confirmPassword"
            type="password"
            show-password
          ></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="handleCancelPassword">取消</el-button>
        <el-button type="primary" @click="handleConfirmPassword" :loading="loading">
          确定
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { setMobileScrollLock } from '@/utils/mobileScrollLock';
import { isMobileViewport, watchMobileViewport } from '@/utils/mobileViewport';
import Message from "../message/index.vue";
import { getCurrentUserInfo, updatePassword, getNoticeTotal } from "@/api/login";

export default {
  name: "SidebarItem",
  components: {
    Message
  },
  data() {
    const validateConfirmPassword = (rule, value, callback) => {
      if (value !== this.editFormData.newPassword) {
        callback(new Error('两次输入的密码不一致'));
      } else {
        callback();
      }
    };
    
    return {
      dialogVisible: false,
      loading: false,
      editFormData: {
        oldPassword: "",
        newPassword: "",
        confirmPassword: ""
      },
      passwordRules: {
        oldPassword: [
          { required: true, message: '请输入旧密码', trigger: 'blur' }
        ],
        newPassword: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          { min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, message: '请确认新密码', trigger: 'blur' },
          { validator: validateConfirmPassword, trigger: 'blur' }
        ]
      },
      visibleSubmenus: {},
      hoverItem: null,
      nickName: null,
      userName: null,
      emailName: null,
      icon: null,
      noticeCount: 0,
      mobileMenuOpen: false,
      mobileExpandedMenu: '',
      // 添加一个存储每个菜单项宽度的对象
      menuItemWidths: {}
    };
  },
  props: {
    routes: {
      type: Array,
      required: true
    },
    isNest: {
      type: Boolean,
      default: false
    }
  },
  mounted() {
    this.getUserInfo();
    this.getNoticeCount();
    window.addEventListener('keydown', this.handleMobileMenuKeydown);
    this._jlStopMenuViewport = watchMobileViewport(mobile => { if (!mobile) this.closeMobileMenu(); });
    // 获取菜单项宽度
    this.$nextTick(() => {
      this.calculateMenuItemWidths();
    });
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleMobileMenuKeydown);
    if (this._jlStopMenuViewport) this._jlStopMenuViewport();
    setMobileScrollLock(this, false);
  },
  computed: {
    ...mapGetters(["sidebar", "avatar", "name"]),
    visibleRoutes() {
      return this.routes.filter(
        item => this.isVisibleRoute(item) && item.children && this.visibleChildren(item.children).length > 0
      );
    }
  },
  watch: {
    mobileMenuOpen(open) {
      setMobileScrollLock(this, open);
    },
    '$route'() {
      this.closeMobileMenu();
    }
  },
  methods: {
    isDirectPageMenu(item) {
      return item && item.name === 'shouye';
    },
    isWideSubmenu(item) {
      if (this.isDirectPageMenu(item)) return false;
      const groups = this.visibleChildren(item && item.children);
      const pageCount = groups.reduce((total, group) => total + this.visibleThirdChildren(group).length, 0);
      return groups.length >= 5 || (groups.length === 3 && pageCount >= 12);
    },
    getDirectPageIcon(child) {
      return child && child.name === 'materialAnalysis'
        ? 'el-icon-data-analysis'
        : 'el-icon-s-home';
    },
    isActiveDirectPage(item, child) {
      return this.$route.path === this.getSecondLevelTargetPath(item, child);
    },
    visibleChildren(children) {
      return (children || []).filter(child => {
        if (!this.isVisibleRoute(child)) {
          return false;
        }
        if (child.children && child.children.length > 0) {
          return this.visibleThirdChildren(child).length > 0;
        }
        return true;
      });
    },
    visibleThirdChildren(child) {
      if (!child || !child.children) {
        return [];
      }
      return child.children.filter(subChild => this.isVisibleRoute(subChild));
    },
    isVisibleRoute(route) {
      return route && !route.hidden;
    },
    getSecondLevelTargetPath(item, child) {
      const firstVisibleThirdChild = this.visibleThirdChildren(child)[0];
      if (firstVisibleThirdChild) {
        return this.generatePath(child, firstVisibleThirdChild);
      }
      return this.generatePath(item, child);
    },
    getMenuInitial(item) {
      const title = item && item.meta && item.meta.title;
      return title ? String(title).substring(0, 1) : '导';
    },
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen;
      if (this.mobileMenuOpen && !this.mobileExpandedMenu) {
        const activeItem = this.visibleRoutes.find(item => this.isActive(item));
        const firstItem = activeItem || this.visibleRoutes[0];
        this.mobileExpandedMenu = firstItem ? firstItem.name : '';
      }
    },
    closeMobileMenu() {
      this.mobileMenuOpen = false;
    },
    toggleMobileSection(name) {
      this.mobileExpandedMenu = this.mobileExpandedMenu === name ? '' : name;
    },
    handleMobileMenuKeydown(event) {
      if (event.key === 'Escape' && this.mobileMenuOpen) {
        this.closeMobileMenu();
      }
    },
    showSubmenu(name) {
      if (isMobileViewport()) {
        this.mobileExpandedMenu = name;
        this.mobileMenuOpen = true;
        return;
      }
      this.$set(this.visibleSubmenus, name, true);
      // 显示下拉菜单后计算位置
      this.$nextTick(() => {
        this.adjustSubmenuPosition(name);
      });
    },
    hideSubmenu(name) {
      this.$set(this.visibleSubmenus, name, false);
    },
    isSubmenuVisible(name) {
      return this.visibleSubmenus[name] || false;
    },
    isActive(item) {
      const activePath = this.$route.matched.length
        ? this.$route.matched[0].path
        : "";
      return activePath === item.path;
    },
    isActiveChild(child) {
      return this.$route.path === child.path;
    },
    handleMouseEnter(item) {
      this.hoverItem = item.name;
    },
    handleMouseLeave() {
      this.hoverItem = null;
    },
    generatePath(item, child) {
      let path;
      if (child.morePath) {
        path = child.morePath;
      } else {
        let basePath = item.path;
        let childPath = child.path;
        if (basePath.endsWith("/")) {
          basePath = basePath.slice(0, -1);
        }
        if (childPath.startsWith("/")) {
          childPath = childPath.slice(1);
        }
        path = `${basePath}/${childPath}`;
      }
      return path;
    },
    logout() {
      this.$store.dispatch("LogOut").then(() => {
        location.reload();
      });
    },
    handleModifyPassword() {
      this.dialogVisible = true;
    },
    async handleConfirmPassword() {
      try {
        await this.$refs.passwordForm.validate();
        
        this.loading = true;
        this.editFormData.username = this.userName;
        
        await updatePassword(this.editFormData);
        
        this.$message.success("密码修改成功，请重新登录");
        this.dialogVisible = false;
        this.editFormData = {
          oldPassword: "",
          newPassword: "",
          confirmPassword: ""
        };
        
        // 延迟退出，让用户看到成功提示
        setTimeout(() => {
          this.logout();
        }, 1500);
      } catch (error) {
        console.error("修改密码失败:", error);
      } finally {
        this.loading = false;
      }
    },
    handleCancelPassword() {
      this.dialogVisible = false;
      this.$refs.passwordForm.resetFields();
    },
    getUserInfo() {
      getCurrentUserInfo().then(response => {
        const data = response.data;
        this.nickName = data.nickName;
        this.userName = data.username;
        this.emailName = data.email;
        this.icon = data.icon;
      });
    },
    getNoticeCount() {
      getNoticeTotal()
        .then(response => {
          if (response.code === 200 && response.data) {
            this.noticeCount = response.data.noticeCount || 0;
          } else {
            this.noticeCount = 0;
          }
        })
        .catch(error => {
          console.error("获取通知数量失败:", error);
          this.noticeCount = 0;
        });
    },
    handleNoticeVisibleChange(visible) {
      if (!visible) {
        return;
      }
      this.getNoticeCount();
      this.$nextTick(() => {
        if (this.$refs.noticeMessage && this.$refs.noticeMessage.getList) {
          this.$refs.noticeMessage.getList();
        }
      });
    },
    // 计算菜单项宽度
    calculateMenuItemWidths() {
      const menuItems = document.querySelectorAll('.menu-item');
      menuItems.forEach((item, index) => {
        const route = this.visibleRoutes[index];
        if (route) {
          this.menuItemWidths[route.name] = item.offsetWidth;
        }
      });
    },
    // 调整下拉菜单位置
    adjustSubmenuPosition(name) {
      const submenu = this.$refs[`submenu_${name}`];
      if (submenu && submenu[0]) {
        const submenuEl = submenu[0];
        const menuItemWidth = this.menuItemWidths[name] || 0;
        const submenuWidth = submenuEl.offsetWidth;

        // 默认相对一级菜单居中，并在临近窗口边缘时留出安全间距
        let leftPosition = menuItemWidth / 2 - submenuWidth / 2;
        const parentRect = submenuEl.parentElement.getBoundingClientRect();
        const viewportPadding = 16;
        const expectedLeft = parentRect.left + leftPosition;

        if (expectedLeft < viewportPadding) {
          leftPosition += viewportPadding - expectedLeft;
        } else if (expectedLeft + submenuWidth > window.innerWidth - viewportPadding) {
          leftPosition -= expectedLeft + submenuWidth - (window.innerWidth - viewportPadding);
        }

        submenuEl.style.left = `${leftPosition}px`;
      }
    }
  }
};
</script>

<style scoped>
.menu-wrapper {
  --nav-primary: hsl(208, 100%, 50%);
  --nav-primary-strong: hsl(211, 100%, 43%);
  --nav-text: hsl(220, 28%, 18%);
  --nav-text-secondary: hsl(218, 14%, 42%);
  --nav-text-tertiary: hsl(218, 13%, 60%);
  --nav-surface: hsl(210, 33%, 99%);
  --nav-surface-hover: hsl(216, 33%, 97%);
  --nav-border: hsl(216, 25%, 92%);
  display: flex;
  align-items: center;
  width: 100%;
  height: 64px;
  padding: 0 24px;
  color: var(--nav-text);
  background: rgba(255, 255, 255, 0.98);
  box-sizing: border-box;
}

/* 品牌区：保持清晰识别，不与业务导航争夺视觉焦点 */
.logoContent {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  margin-right: 32px;
}

.logo-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.logo {
  width: 30px;
  height: 30px;
  object-fit: contain;
}

.logo-text {
  color: var(--nav-text);
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
  white-space: nowrap;
}

/* 一级导航：默认退后，当前模块和交互状态使用品牌色 */
.menuContent {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  height: 100%;
  gap: 4px;
}

.menu-item {
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
}

.menu-item-link {
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  font-family: inherit;
  text-decoration: none;
  outline: none;
}

.menu-item-content {
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  padding: 0 14px;
  border-radius: 8px;
  color: var(--nav-text-secondary);
  cursor: pointer;
  box-sizing: border-box;
  transition: color 160ms ease, background-color 160ms ease;
}

.menu-item-content:hover {
  color: var(--nav-text);
  background: var(--nav-surface-hover);
}

.menu-item-content.is-active {
  color: var(--nav-primary-strong);
}

.menu-item-content.is-active::after {
  content: '';
  position: absolute;
  left: 50%;
  bottom: -12px;
  width: 24px;
  height: 3px;
  border-radius: 3px 3px 0 0;
  background: var(--nav-primary);
  transform: translateX(-50%);
}

.menu-text {
  color: inherit;
  font-size: 14px;
  line-height: 20px;
  font-weight: 700;
  white-space: nowrap;
}

.icon-arrow {
  width: 0;
  height: 0;
  border-left: 3.5px solid transparent;
  border-right: 3.5px solid transparent;
  border-top: 4px solid var(--nav-text-tertiary);
  transition: transform 160ms ease, border-color 160ms ease;
}

.menu-item:hover .icon-arrow {
  border-top-color: var(--nav-text-secondary);
  transform: rotate(180deg);
}

.menu-item-link:focus-visible .menu-item-content,
.menu-item-link:focus .menu-item-content {
  box-shadow: 0 0 0 2px rgba(0, 132, 255, 0.18);
}

/* 多级导航面板：仅浮层使用清晰但克制的深度 */
.submenu-children {
  position: absolute;
  top: calc(100% - 1px);
  left: 0;
  z-index: 1001;
  min-width: 560px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 84px);
  overflow: hidden;
  background: #ffffff;
  border: 1px solid var(--nav-border);
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(31, 49, 77, 0.11), 0 2px 8px rgba(31, 49, 77, 0.05);
  opacity: 0;
  visibility: hidden;
  transform: translateY(6px);
  transition: opacity 180ms ease, visibility 180ms ease, transform 180ms ease;
}

.menu-item:hover .submenu-children,
.menu-item:focus-within .submenu-children,
.submenu-children:hover {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.submenu-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(170px, 1fr));
  gap: 16px 24px;
  max-height: calc(100vh - 84px);
  padding: 20px;
  overflow-y: auto;
  box-sizing: border-box;
}

.submenu-children.is-wide-menu {
  min-width: 720px;
}

.submenu-children.is-wide-menu .submenu-grid {
  grid-template-columns: repeat(3, minmax(170px, 1fr));
}

.submenu-children.is-direct-page-menu {
  min-width: 288px;
}

.direct-page-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px;
}

.direct-page-link {
  display: flex;
  min-height: 42px;
  align-items: center;
  gap: 10px;
  padding: 0 11px;
  color: var(--nav-text-secondary);
  border-radius: 8px;
  text-decoration: none;
  outline: none;
  transition: color 160ms ease, background-color 160ms ease;
}

.direct-page-link:hover,
.direct-page-link:focus,
.direct-page-link.is-active {
  color: var(--nav-primary-strong);
  background: hsl(208, 100%, 97%);
}

.direct-page-icon {
  width: 18px;
  flex: 0 0 18px;
  color: var(--nav-text-tertiary);
  font-size: 16px;
  text-align: center;
}

.direct-page-link:hover .direct-page-icon,
.direct-page-link:focus .direct-page-icon,
.direct-page-link.is-active .direct-page-icon {
  color: var(--nav-primary);
}

.direct-page-link > span {
  min-width: 0;
  flex: 1;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
}

.direct-page-link > i:last-child {
  color: var(--nav-text-tertiary);
  font-size: 11px;
}

.submenu-column {
  min-width: 0;
}

.submenu-column-header {
  margin-bottom: 6px;
}

.column-title-link {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 36px;
  padding: 0 8px;
  color: var(--nav-text);
  text-decoration: none;
  border-radius: 8px;
  outline: none;
  transition: color 160ms ease, background-color 160ms ease;
}

.column-title-link:hover,
.column-title-link:focus {
  color: var(--nav-primary-strong);
  background: var(--nav-surface-hover);
}

.column-title-link.is-active {
  color: var(--nav-primary-strong);
  background: hsl(208, 100%, 97%);
}

.column-icon {
  flex: 0 0 auto;
  color: var(--nav-text-tertiary);
  font-size: 15px;
}

.column-title-link:hover .column-icon,
.column-title-link:focus .column-icon,
.column-title-link.is-active .column-icon {
  color: var(--nav-primary);
}

.column-title {
  min-width: 0;
  color: inherit;
  font-size: 14px;
  line-height: 20px;
  font-weight: 700;
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  word-break: break-word;
}

.third-level-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.third-level-item {
  display: flex;
  align-items: center;
  gap: 9px;
  min-height: 34px;
  padding: 0 10px;
  color: var(--nav-text-secondary);
  font-size: 13px;
  line-height: 18px;
  font-weight: 400;
  text-decoration: none;
  border-radius: 8px;
  outline: none;
  transition: color 160ms ease, background-color 160ms ease;
}

.third-level-item:hover,
.third-level-item:focus {
  color: var(--nav-text);
  background: var(--nav-surface-hover);
}

.third-level-item.is-active {
  color: var(--nav-primary-strong);
  background: hsl(208, 100%, 97%);
  font-weight: 700;
}

.icon-dot {
  width: 4px;
  height: 4px;
  flex: 0 0 4px;
  border-radius: 50%;
  background: hsl(216, 14%, 77%);
}

.third-level-item:hover .icon-dot,
.third-level-item:focus .icon-dot {
  background: var(--nav-text-tertiary);
}

.third-level-item.is-active .icon-dot {
  background: var(--nav-primary);
}

/* 右侧操作区：使用留白分组，不使用常驻卡片边框 */
.userContent {
  position: relative;
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 4px;
  margin-left: auto;
  padding-left: 25px;
}

.userContent::before {
  content: '';
  position: absolute;
  left: 12px;
  top: 50%;
  width: 1px;
  height: 20px;
  background: var(--nav-border);
  transform: translateY(-50%);
}

.dropdown-notice,
.dropdown-user {
  cursor: pointer;
}

.notice-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 0;
  color: var(--nav-text-secondary);
  border: 0;
  background: transparent;
  font-family: inherit;
  border-radius: 8px;
  outline: none;
  cursor: pointer;
  transition: color 160ms ease, background-color 160ms ease, box-shadow 160ms ease;
}

.notice-wrapper:hover {
  color: var(--nav-primary-strong);
  background: var(--nav-surface-hover);
}

.notice-wrapper:focus-visible,
.notice-wrapper:focus {
  color: var(--nav-primary-strong);
  box-shadow: 0 0 0 2px rgba(0, 132, 255, 0.18);
}

.icon-notice {
  display: block;
  color: inherit;
  font-size: 18px;
}

.notice-dropdown-item {
  padding: 0;
}

.user-wrapper {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 142px;
  height: 44px;
  padding: 0 8px 0 6px;
  color: var(--nav-text);
  border: 0;
  background: transparent;
  font-family: inherit;
  border-radius: 8px;
  outline: none;
  cursor: pointer;
  box-sizing: border-box;
  transition: background-color 160ms ease, box-shadow 160ms ease;
}

.user-wrapper:hover {
  background: var(--nav-surface-hover);
}

.user-wrapper:focus-visible,
.user-wrapper:focus {
  box-shadow: 0 0 0 2px rgba(0, 132, 255, 0.18);
}

.user-avatar,
.profile-avatar {
  color: #ffffff;
  background: hsl(208, 100%, 50%);
  font-weight: 700;
  box-shadow: inset 0 0 0 1px rgba(0, 74, 144, 0.08);
}

.user-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 1px;
}

.user-name {
  max-width: 92px;
  color: var(--nav-text);
  font-size: 13px;
  line-height: 18px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-role {
  color: var(--nav-text-tertiary);
  font-size: 11px;
  line-height: 14px;
  font-weight: 400;
}

.icon-arrow-down {
  width: 0;
  height: 0;
  margin-left: auto;
  border-left: 3.5px solid transparent;
  border-right: 3.5px solid transparent;
  border-top: 4px solid var(--nav-text-tertiary);
  transition: transform 160ms ease, border-color 160ms ease;
}

.user-wrapper:hover .icon-arrow-down {
  border-top-color: var(--nav-text-secondary);
  transform: rotate(180deg);
}

/* 用户下拉菜单 */
.user-dropdown-menu {
  min-width: 240px;
  padding: 6px 0;
  margin-top: 8px;
  border: 1px solid hsl(216, 25%, 92%);
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(31, 49, 77, 0.11), 0 2px 8px rgba(31, 49, 77, 0.05);
}

.dropdown-header {
  padding: 14px 16px;
  cursor: default;
}

.dropdown-header:hover {
  background: transparent !important;
}

.user-profile {
  display: flex;
  align-items: center;
  gap: 12px;
}

.profile-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 3px;
}

.profile-name {
  color: hsl(220, 28%, 18%);
  font-size: 14px;
  line-height: 20px;
  font-weight: 700;
}

.profile-email {
  max-width: 160px;
  color: hsl(218, 14%, 42%);
  font-size: 12px;
  line-height: 18px;
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 38px;
  padding: 0 12px;
  margin: 0 6px;
  color: hsl(218, 14%, 42%);
  font-size: 13px;
  font-weight: 400;
  border-radius: 8px;
  cursor: pointer;
  transition: color 160ms ease, background-color 160ms ease;
}

.dropdown-item:hover {
  color: hsl(220, 28%, 18%);
  background: hsl(216, 33%, 97%);
}

.icon-setting,
.icon-logout {
  width: 17px;
  color: hsl(218, 13%, 60%);
  font-size: 16px;
  text-align: center;
  background: none;
}

.dropdown-item:hover .icon-setting,
.dropdown-item:hover .icon-logout {
  color: hsl(208, 100%, 50%);
  background: none;
}

.menu-wrapper >>> .el-badge__content {
  height: 16px;
  min-width: 16px;
  padding: 0 4px;
  color: #ffffff;
  background: hsl(354, 76%, 53%);
  border: 2px solid #ffffff;
  font-size: 10px;
  line-height: 12px;
  font-weight: 700;
  box-sizing: border-box;
}

.mobile-menu-toggle,
.mobile-menu-backdrop {
  display: none;
}

@media (max-width: 1280px) {
  .menu-wrapper {
    padding: 0 16px;
  }

  .logoContent {
    margin-right: 16px;
  }

  .logo-text {
    display: none;
  }

  .menu-item-content {
    padding: 0 11px;
  }

  .userContent {
    padding-left: 21px;
  }

  .userContent::before {
    left: 10px;
  }

  .submenu-children {
    min-width: 500px;
  }

  .submenu-grid {
    grid-template-columns: repeat(2, minmax(170px, 1fr));
  }
}

@media (max-width: 960px) {
  .menu-wrapper {
    padding: 0 12px;
  }

  .logoContent {
    margin-right: 8px;
  }

  .logo {
    width: 28px;
    height: 28px;
  }

  .menuContent {
    gap: 0;
  }

  .menu-item-content {
    padding: 0 8px;
  }

  .menu-text {
    font-size: 13px;
  }

  .userContent {
    gap: 2px;
    padding-left: 15px;
  }

  .userContent::before {
    left: 7px;
  }

  .user-info,
  .icon-arrow-down {
    display: none;
  }

  .user-wrapper {
    width: 44px;
    min-width: 44px;
    padding: 0 6px;
  }
}

@media (max-width: 768px), (max-width: 960px) and (max-height: 540px) and (orientation: landscape) {
  .menu-wrapper {
    align-content: flex-start;
    flex-wrap: wrap;
    height: 104px;
    padding-right: max(12px, env(safe-area-inset-right));
    padding-left: max(12px, env(safe-area-inset-left));
  }

  .logoContent {
    order: 1;
    display: flex;
    height: 60px;
    margin-right: 0;
  }

  .logo {
    width: 30px;
    height: 30px;
  }

  .menuContent {
    order: 3;
    display: flex;
    width: calc(100% + 24px);
    height: 44px;
    flex: 0 0 calc(100% + 24px);
    gap: 4px;
    margin: 0 -12px;
    padding: 4px 8px;
    border-top: 1px solid hsl(216, 25%, 93%);
    background: hsl(216, 33%, 98%);
    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior-x: contain;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }

  .menuContent::-webkit-scrollbar {
    display: none;
  }

  .menu-item,
  .menu-item-link {
    height: 36px;
  }

  .menu-item {
    flex: 0 0 auto;
  }

  .menu-item-content {
    height: 36px;
    padding: 0 13px;
    border: 1px solid transparent;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 1px 2px rgba(31, 49, 77, 0.04);
  }

  .menu-item-content.is-active::after {
    display: none;
  }

  .menu-item-content.is-active,
  .menu-item-link[aria-expanded="true"] .menu-item-content {
    border-color: hsl(208, 88%, 88%);
    color: var(--nav-primary-strong);
    background: hsl(208, 100%, 96%);
    box-shadow: none;
  }

  .menu-item-link[aria-expanded="true"] .icon-arrow {
    border-top-color: var(--nav-primary);
    transform: rotate(180deg);
  }

  .menu-text {
    font-size: 13px;
    line-height: 18px;
  }

  .userContent {
    order: 2;
    height: 60px;
    margin-left: auto;
  }

  .userContent::before {
    display: none;
  }

  .mobile-menu-toggle {
    position: relative;
    order: 2;
    display: flex;
    width: 44px;
    height: 44px;
    flex: 0 0 44px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 5px;
    margin-left: 4px;
    padding: 0;
    border: 0;
    border-radius: 8px;
    color: var(--nav-text-secondary);
    background: transparent;
    outline: none;
    cursor: pointer;
    transition: background-color 160ms ease, box-shadow 160ms ease;
  }

  .mobile-menu-toggle:hover,
  .mobile-menu-toggle:focus {
    background: var(--nav-surface-hover);
  }

  .mobile-menu-toggle:focus-visible {
    box-shadow: 0 0 0 2px rgba(0, 132, 255, 0.18);
  }

  .mobile-menu-toggle span {
    display: block;
    width: 19px;
    height: 2px;
    border-radius: 2px;
    background: currentColor;
    transition: transform 180ms ease, opacity 180ms ease;
  }

  .mobile-menu-toggle.is-open span:nth-child(1) {
    transform: translateY(7px) rotate(45deg);
  }

  .mobile-menu-toggle.is-open span:nth-child(2) {
    opacity: 0;
  }

  .mobile-menu-toggle.is-open span:nth-child(3) {
    transform: translateY(-7px) rotate(-45deg);
  }

  .mobile-menu-backdrop {
    position: fixed;
    z-index: 1002;
    top: calc(104px + env(safe-area-inset-top));
    right: 0;
    bottom: auto;
    left: 0;
    display: block;
    height: calc(100vh - 104px - env(safe-area-inset-top));
    height: calc(100dvh - 104px - env(safe-area-inset-top));
    height: calc(var(--app-viewport-height, 100dvh) - 104px - env(safe-area-inset-top));
    background: rgba(24, 39, 58, 0.28);
    backdrop-filter: blur(2px);
  }

  .mobile-menu-panel {
    width: 100%;
    height: 100%;
    padding: 0 max(12px, env(safe-area-inset-right)) max(16px, env(safe-area-inset-bottom)) max(12px, env(safe-area-inset-left));
    overflow-y: auto;
    overscroll-behavior: contain;
    background: hsl(216, 33%, 98%);
    box-sizing: border-box;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }

  .mobile-menu-panel::-webkit-scrollbar {
    display: none;
  }

  .mobile-menu-heading {
    position: sticky;
    z-index: 1;
    top: 0;
    display: flex;
    min-height: 72px;
    align-items: center;
    justify-content: space-between;
    padding: 8px 2px;
    background: hsl(216, 33%, 98%);
  }

  .mobile-menu-heading strong,
  .mobile-menu-heading span {
    display: block;
  }

  .mobile-menu-heading strong {
    color: var(--nav-text);
    font-size: 18px;
    line-height: 24px;
    font-weight: 700;
  }

  .mobile-menu-heading span {
    margin-top: 3px;
    color: var(--nav-text-secondary);
    font-size: 12px;
    line-height: 18px;
  }

  .mobile-menu-heading button {
    display: flex;
    width: 44px;
    height: 44px;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    border-radius: 8px;
    color: var(--nav-text-secondary);
    background: #ffffff;
    box-shadow: 0 1px 3px rgba(31, 49, 77, 0.08);
  }

  .mobile-menu-heading button i {
    font-size: 18px;
  }

  .mobile-nav-list {
    padding-bottom: 12px;
  }

  .mobile-nav-section {
    margin-bottom: 8px;
    overflow: hidden;
    border: 1px solid var(--nav-border);
    border-radius: 10px;
    background: #ffffff;
  }

  .mobile-nav-section.is-active {
    background: hsl(208, 100%, 98%);
  }

  .mobile-nav-primary {
    display: flex;
    width: 100%;
    min-height: 54px;
    align-items: center;
    padding: 5px 14px;
    border: 0;
    color: var(--nav-text);
    background: transparent;
    font-family: inherit;
    text-align: left;
  }

  .mobile-nav-primary-icon {
    display: flex;
    width: 32px;
    height: 32px;
    flex: 0 0 32px;
    align-items: center;
    justify-content: center;
    margin-right: 10px;
    border-radius: 8px;
    color: var(--nav-primary-strong);
    background: hsl(208, 100%, 96%);
    font-size: 13px;
    font-weight: 700;
  }

  .mobile-nav-primary-text {
    min-width: 0;
    flex: 1;
    font-size: 15px;
    line-height: 22px;
    font-weight: 700;
  }

  .mobile-nav-primary > i {
    margin-left: 12px;
    color: var(--nav-text-tertiary);
    font-size: 12px;
    transition: transform 180ms ease;
  }

  .mobile-nav-section.is-expanded .mobile-nav-primary > i {
    transform: rotate(180deg);
  }

  .mobile-nav-children {
    padding: 0 10px 10px 52px;
  }

  .mobile-nav-group {
    padding: 5px 0;
    border-top: 1px solid hsl(216, 25%, 94%);
  }

  .mobile-nav-page,
  .mobile-nav-secondary,
  .mobile-nav-third {
    display: flex;
    min-height: 44px;
    align-items: center;
    border-radius: 8px;
    text-decoration: none;
    outline: none;
  }

  .mobile-nav-page {
    gap: 8px;
    padding: 0 10px;
    color: var(--nav-text);
    font-size: 14px;
    line-height: 20px;
    font-weight: 600;
  }

  .mobile-nav-page + .mobile-nav-page {
    border-top: 1px solid hsl(216, 25%, 94%);
  }

  .mobile-nav-page > i:first-child {
    color: var(--nav-text-tertiary);
    font-size: 15px;
  }

  .mobile-nav-page > span {
    min-width: 0;
    flex: 1;
  }

  .mobile-nav-page > i:last-child {
    color: var(--nav-text-tertiary);
    font-size: 11px;
  }

  .mobile-nav-secondary {
    gap: 8px;
    padding: 0 10px;
    color: var(--nav-text);
    font-size: 14px;
    line-height: 20px;
    font-weight: 700;
  }

  .mobile-nav-secondary > i:first-child {
    color: var(--nav-text-tertiary);
    font-size: 15px;
  }

  .mobile-nav-secondary > span {
    min-width: 0;
    flex: 1;
  }

  .mobile-nav-secondary > i:last-child {
    color: var(--nav-text-tertiary);
    font-size: 11px;
  }

  .mobile-nav-page.is-active,
  .mobile-nav-page:focus,
  .mobile-nav-secondary.is-active,
  .mobile-nav-secondary:focus,
  .mobile-nav-third.is-active,
  .mobile-nav-third:focus {
    color: var(--nav-primary-strong);
    background: hsl(208, 100%, 96%);
  }

  .mobile-nav-third-list {
    padding: 0 0 3px 16px;
  }

  .mobile-nav-third {
    gap: 10px;
    padding: 0 10px;
    color: var(--nav-text-secondary);
    font-size: 13px;
    line-height: 20px;
    font-weight: 400;
  }

  .mobile-menu-fade-enter-active,
  .mobile-menu-fade-leave-active {
    transition: opacity 180ms ease;
  }

  .mobile-menu-fade-enter,
  .mobile-menu-fade-leave-to {
    opacity: 0;
  }
}

@media (max-width: 480px) {
  .menu-wrapper {
    padding-right: max(8px, env(safe-area-inset-right));
    padding-left: max(8px, env(safe-area-inset-left));
  }

  .userContent {
    padding-left: 9px;
  }

  .userContent::before {
    left: 4px;
  }

  .notice-wrapper,
  .user-wrapper {
    width: 40px;
    min-width: 40px;
  }

  .mobile-menu-panel {
    padding-right: max(8px, env(safe-area-inset-right));
    padding-left: max(8px, env(safe-area-inset-left));
  }
}

@media (prefers-reduced-motion: reduce) {
  .menu-item-content,
  .icon-arrow,
  .submenu-children,
  .column-title-link,
  .third-level-item,
  .notice-wrapper,
  .user-wrapper,
  .icon-arrow-down,
  .dropdown-item,
  .mobile-menu-toggle span,
  .mobile-nav-primary > i,
  .mobile-menu-fade-enter-active,
  .mobile-menu-fade-leave-active {
    transition: none;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/layout/components/topSidebar/topSidebarItem.vue